import React from "react";
import useToasts from "@ui/Toast/useToasts";
import ModalForm from "@ui/Modal/Form";
import t from "../../../../i18n";
import { useDelete } from "../../../../api/useCall";
import { useNavigate } from "react-router";
import { useSetups, type Setup } from "../../../../api/useSetups";
import type { Project } from "../../../../api/useProjects";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import Text from "@ui/Text";
import { stringComparator, withNull, withOrder } from "diagnostics-lib/comparator";
import { comparatorBy } from "diagnostics-lib/comparator";
import { useImpactedAnalyses } from "./useAnalysisImpacted";
import useActiveAnalysesStream from "../../../../api/useActiveAnalysesStream";
import { toPath } from "../../../../router/helpers/path";

interface P {
  project: Project;
  setup: Setup;
  setups: Setup[];
  onRequestClose: () => void;
}

const analysisStatus = new Set([
  "SIMULATION",
  "SIMULATION_ERROR",
  "DIAGNOSIS",
  "DIAGNOSIS_ERROR",
] as const);

const DeleteModalForm = ({ project, setup, setups, onRequestClose }: P) => {
  const { addToast } = useToasts();
  const deleteSetup = useDelete("/projects/:projectId(uuid)/setups/:setupId(uuid)");
  const navigate = useNavigate();
  const setupId = setup.id;
  const projectId = project.id;

  const setupsSorted = React.useMemo(
    () =>
      [...setups].sort(
        comparatorBy("startDate", withNull(withOrder(stringComparator, "asc"), "first")),
      ),
    [setups],
  );

  const currentSetupIndex = setupsSorted.findIndex(f => setupId === f.id);
  const previousSetup = setupsSorted[currentSetupIndex - 1];
  const nextSetup = setupsSorted[currentSetupIndex + 1];
  const { activeAnalyses } = useActiveAnalysesStream(projectId);

  const impactedAnalyses = useImpactedAnalyses({
    setupId,
    setups: useSetups(projectId),
    activeAnalyses,
    analysisStatus,
  });

  const showAdditionalMessage = setup.simulationStatus === "VALID" && impactedAnalyses.length > 0;

  const handleSubmit = React.useCallback(async () => {
    await deleteSetup({ routeParams: { projectId, setupId } });
    addToast("success", t("setups.modal.toast.delete_setup", { name: setup.name }));
    void navigate(toPath("/projects/:projectId/settings", { projectId }));
    return true;
  }, [addToast, deleteSetup, navigate, projectId, setupId, setup.name]);

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={handleSubmit}
      focusCancel
      okButtonVariant="destructive"
      okButtonName={t("modal.delete")}
    >
      <Text>
        {setupsSorted.length > 1
          ? t("setups.modal.delete.setup.infos", {
              currentSetupName: setup.name,
              linkedSetupName: nextSetup?.name ?? previousSetup?.name,
            })
          : t("setups.modal.delete.last_setup.infos", {
              currentSetupName: setup.name,
            })}
      </Text>
      {showAdditionalMessage ? (
        <AdditionnalMessage
          status="WARNING"
          text={t("setup.modal.delete.analysis.additionnal_message.warning", {
            simulationValid: setup.simulationStatus === "VALID",
            diagnosisValid: setup.diagnosisStatus === "COMPLETE",
            impactedAnalysisCount: impactedAnalyses.length,
          })}
        />
      ) : null}
    </ModalForm>
  );
};

export default React.memo(DeleteModalForm);
